import React from "react";
import { TaskListReducer, initialState, Actions } from "./reducer";

const TaskListContext = React.createContext(null);

const TaskListProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(TaskListReducer, initialState);
  const more = React.useRef(() => null);
  const less = React.useRef(() => null);

  const setMore = React.useCallback((cb) => {
    if (cb) {
      // cb should return a function
      more.current = cb();
    } else {
      more.current = () => null;
    }
  }, []);

  const setLess = React.useCallback((cb) => {
    if (cb) {
      // cb should return a function
      less.current = cb();
    } else {
      less.current = () => null;
    }
  }, []);

  const reset = React.useCallback(() => {
    dispatch({ type: Actions.RESET });
  }, []);

  const setIDs = React.useCallback((ids, current) => {
    dispatch({
      type: Actions.SET_LIST,
      list: ids,
      current,
      less: less.current,
      more: more.current,
    });
  }, []);

  const moveForward = React.useCallback((cb) => {
    dispatch({ type: Actions.NEXT, cb, more: more.current });
  }, []);

  const moveBackward = React.useCallback((cb) => {
    dispatch({ type: Actions.PREV, cb, less: less.current });
  }, []);

  const addTasks = React.useCallback((id) => {
    const tasks = Array.isArray(id) ? id : [id];
    dispatch({ type: Actions.ADD_TASKS, tasks });
  }, []);

  const addTasksFront = React.useCallback((id) => {
    const tasks = Array.isArray(id) ? id : [id];
    dispatch({ type: Actions.ADD_TASKS_FRONT, tasks });
  }, []);

  const pushFrame = React.useCallback((ids, current) => {
    dispatch({ type: Actions.PUSH_FRAME, list: ids, current });
  }, []);

  const popFrame = React.useCallback((cb) => {
    dispatch({ type: Actions.POP_FRAME, cb });
  }, []);

  const popOnBack = React.useCallback(
    ({ cb, next }) => {
      if (state.stack.length === 0) {
        return;
      }

      const prevStack = state.stack[0];
      if (prevStack.taskIDs.includes(next)) {
        dispatch({ type: Actions.POP_FRAME, cb });
      }
    },
    [state],
  );

  return (
    <TaskListContext.Provider
      value={{
        canScrollLeft: state.current.canScrollLeft,
        canScrollRight: state.current.canScrollRight,
        currentID: state.current.currentTask,
        setLess,
        setMore,
        reset,
        setIDs,
        moveForward,
        moveBackward,
        addTasksFront,
        addTasks,
        pushFrame,
        popFrame,
        popOnBack,
      }}
    >
      {children}
    </TaskListContext.Provider>
  );
};

export default TaskListProvider;

export const useTaskList = () => React.useContext(TaskListContext);
